import { useMemo, useState, useRef } from 'react';
import cm from 'classnames';

import { FileStatus, FileType } from 'services/file.service';
import { ConfigFlags } from 'services/clip.service';
import { useAppSelector } from 'store/store-hooks';
import { selectSelectedFile } from 'components/app/_mediator/app-files.slice';
import { selectClipData, isFileStatusCompleted } from 'components/app/_helpers/_app.helper';
import { Clip, selectFilteredFileClips } from 'components/app/editor/_mediator/editor.slice';
// import { useCssFilterClip } from 'components/app/_mediator/css-filter/use-css-filter-clip';
// import { useCssFilter } from 'components/app/_mediator/css-filter/use-css-filter';
import { useSelectorContext } from 'components/app/selector/_mediator/selector.provider';

import { ReactComponent as ErrFileIco } from 'assets/ui/broken-file.svg';
import { PanZoom } from 'components/app/editor/pan-zoom/pan-zoom';
import { TsSlider } from 'components/app/editor/timestamp/ts-slider';
import { SrPlayerVideo } from './sr-player-video';
import { SrPlayerImg } from './sr-player-img';
import styles from './_selector-player.module.scss';

export const SrPlayer = () => {
  const { srPlayerRef, isHighlightSensitivityActive } = useSelectorContext();
  const { srPlayerPlayback } = useAppSelector((state) => state.selector);
  const { clipsMap, activeClip, configPanel } = useAppSelector((state) => state.editor);
  const configs = useAppSelector((state) => state.app__files.configs);
  const statuses = useAppSelector((state) => state.app__files.statuses);
  const file = useAppSelector(selectSelectedFile);
  const imgRef = useRef<HTMLImageElement>(null);
  const [init, setInit] = useState(false);
  const [isTsSliderHovered, setIsTsSliderHovered] = useState(false);

  const clipData = useMemo(() => {
    if (!activeClip || !clipsMap) return;
    return selectClipData(activeClip, clipsMap);
  }, [activeClip, activeClip && clipsMap?.[activeClip]]);

  const fileConfig = useMemo(() => {
    if (!file) return;
    return configs[file.id];
  }, [file, file && configs?.[file.id]]);

  // Get highlights/clips for the current file (only on highlights/selector page)
  const clipIds = useAppSelector(selectFilteredFileClips);

  // Convert clip IDs to Clip objects
  // Optimization: Only recalculate if clipIds content actually changed (not just reference)
  const prevClipIdsRef = useRef<typeof clipIds>();
  const prevHighlightsRef = useRef<Clip[]>([]);
  const highlights = useMemo(() => {
    if (!clipIds || !clipsMap) {
      prevClipIdsRef.current = clipIds;
      prevHighlightsRef.current = [];
      return [];
    }

    const prev = prevClipIdsRef.current;
    // Check if clipIds content actually changed (by value, not just reference)
    const contentChanged = !prev || prev.length !== clipIds.length || prev.some((id, i) => id !== clipIds[i]);

    // If content hasn't changed, return previous result
    if (!contentChanged) {
      return prevHighlightsRef.current;
    }

    // Content changed, recalculate
    const result = clipIds
      .map((clipId) => selectClipData(clipId, clipsMap))
      .filter((clip): clip is Clip => clip !== null);

    // Store for next comparison
    prevClipIdsRef.current = clipIds;
    prevHighlightsRef.current = result;
    return result;
  }, [clipIds, clipsMap]);

  // Remove CSS filter from container - let EnhancedVideoPlayer handle brightness/contrast internally
  // const brightness = useCssFilterClip('brightness', clipData, fileConfig);
  // const contrast = useCssFilterClip('contrast', clipData, fileConfig);

  // const containerCss = useCssFilter({ brightness, contrast });
  // Remove the rotate transform from the container - let EnhancedVideoPlayer handle it
  // useCssRotate(containerCss, { clipData, fileConfig }, true);

  const isImage = useMemo(() => {
    return file?.fileType === FileType.image;
  }, [file]);

  const isPanZoomActive = useMemo(() => {
    return clipData?.config.flags.includes(ConfigFlags.panZoomRotate) && configPanel === 'clip';
  }, [clipData, configPanel]);

  const isPendingEffects = useMemo(() => {
    return file?.id && statuses[file.id] === FileStatus.PendingProcessingEffects;
  }, [file, statuses]);

  const isProcessing = useMemo(() => {
    return (
      file && statuses[file.id] !== FileStatus.PendingProcessingEffects && !isFileStatusCompleted(statuses[file.id])
    );
  }, [file, statuses]);

  if (file?.status === FileStatus.Error) {
    return (
      <div className={styles.SrPlayerErr}>
        <ErrFileIco />
        <p>File error</p>
      </div>
    );
  }
  return (
    <div
      className={cm(styles.SrPlayer, {
        [styles['SrPlayer--pendingProcessing']]: isPendingEffects,
        [styles['SrPlayer--processing']]: isProcessing,
      })}
    >
      <div className={styles.SrPlayer__container}>
        {isImage ? (
          <SrPlayerImg ref={imgRef} file={file} setInit={setInit} />
        ) : (
          <SrPlayerVideo
            file={file}
            fileConfig={fileConfig}
            clipData={clipData}
            setInit={setInit}
            init={init}
            isTsSliderHovered={isTsSliderHovered}
            isHighlightSensitivityActive={isHighlightSensitivityActive}
          />
        )}
      </div>
      {!isImage && (
        <TsSlider
          isVisible={!srPlayerPlayback && !isPanZoomActive && !!activeClip}
          onHoverChange={setIsTsSliderHovered}
          highlights={highlights}
          activeClipId={activeClip}
        />
      )}
      <PanZoom element={isImage ? imgRef : srPlayerRef} isActive={!!isPanZoomActive} init={init} />
    </div>
  );
};
